/**
 * Hook useAgGridColumns para Reused/List
 *
 * Define las columnas de AG Grid para la tabla de Recepciones de Rehusados.
 * Incluye cellRenderers personalizados.
 */

import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { ReturnedReception, ReturnedReceptionStatus } from '../models';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Button } from '@shared/components/ui/button';
import { Link } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { generateReusedLabel } from '@shared/utils/reusedLabelGenerator';

// Función utilitaria para formatear fechas
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

//Helper para obtener el nombre del estado
const getStatusName = (status: ReturnedReceptionStatus, t: any): string => {
  switch (status) {
    case ReturnedReceptionStatus.OPEN:
      return t('status.open', 'Abierto');
    case ReturnedReceptionStatus.POR_UBICAR:
      return t('status.to_locate', 'Por ubicar');
    case ReturnedReceptionStatus.CLOSED:
      return t('status.closed', 'Cerrado');
    case ReturnedReceptionStatus.PENDING_OPEN:
      return t('status.pending_open', 'Pendiente');
    default:
      return '-';
  }
};

// Helper para obtener la clase del badge según el estado
const getStatusBadgeClass = (status: ReturnedReceptionStatus): string => {
  switch (status) {
    case ReturnedReceptionStatus.OPEN:
      return 'bg-card-border text-title';
    case ReturnedReceptionStatus.POR_UBICAR:
      return 'bg-bg-warning text-warning';
    case ReturnedReceptionStatus.CLOSED:
      return 'bg-bg-success text-success';
    case ReturnedReceptionStatus.PENDING_OPEN:
      return 'bg-card-border light:bg-bg-warning text-warning';
    default:
      return 'bg-card-border text-title';
  }
};

// Helper para obtener el texto del botón de acción
const getActionButtonText = (status: ReturnedReceptionStatus, t: any): string => {
  if (status === ReturnedReceptionStatus.CLOSED) {
    return t('actions.view', 'Ver');
  }
  return t('actions.receive', 'Recepcionar');
};

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
}

export const useAgGridColumns = ({ selectedColumns = [] }: UseAgGridColumnsProps) => {
  const { t } = useTranslation('reusedModule');

  // Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // COLUMNA: Referencia
      {
        field: 'code',
        headerName: t('table.code', 'Código'),
        sortable: false,
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedReception;
          return <span className="text-sm font-medium text-title">{item.code}</span>;
        },
      },

      // COLUMNA: Estado
      {
        field: 'status',
        headerName: t('table.status', 'Estado'),
        sortable: false,
        valueGetter: (params) => getStatusName(params.data.status, t),
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedReception;
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(item.status)}`}
            >
              {getStatusName(item.status, t)}
            </span>
          );
        },
      },

      // COLUMNA: Fecha de creación
      {
        field: 'created_at',
        headerName: t('table.date', 'Fecha'),
        sortable: false,
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedReception;
          return <span className="text-sm">{formatDate(item.created_at)}</span>;
        },
      },

      // COLUMNA: Cantidad de pedidos
      {
        field: 'orders_count',
        headerName: t('table.orders', 'Pedidos'),
        sortable: false,
        cellRenderer: (params: any) => {
          const item = params.data as ReturnedReception;
          return (
            <span className="text-sm font-medium">
              {item.orders_count || 0} / {item.total_orders}
            </span>
          );
        },
      },
    ];

    // COLUMNA: Acciones
    baseColumns.push({
      headerName: '',
      field: 'actionsReused',
      sortable: false,
      filter: false,
      resizable: false,
      suppressSizeToFit: true,
      maxWidth: 200,
      cellRenderer: (params: any) => {
        const item = params.data as ReturnedReception;
        const actionRoute =
          item.status === ReturnedReceptionStatus.CLOSED ? `/reused/${item.id}/receive` : `/reused/${item.id}/receive`;

        const isPrinterVisible =
          item.status === ReturnedReceptionStatus.CLOSED ||
          item.status === ReturnedReceptionStatus.POR_UBICAR ||
          item.status === ReturnedReceptionStatus.OPEN;

        return (
          <div className="flex items-center justify-center gap-2" style={{ width: '140px' }}>
            <Link to={actionRoute} className={isPrinterVisible ? 'w-24' : 'w-[140px]'}>
              <Button
                size="sm"
                variant={item.status === ReturnedReceptionStatus.OPEN ? 'primary' : 'outline'}
                className="w-full justify-center"
              >
                {getActionButtonText(item.status, t)}
              </Button>
            </Link>
            {isPrinterVisible && (
              <div className="w-9 h-9 flex items-center justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-full p-0 flex items-center justify-center"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const labelUrl = await generateReusedLabel({
                      code: item.code,
                      date: new Date(item.created_at.replace(' ', 'T')).toLocaleDateString('es-ES'),
                    });
                    window.open(labelUrl, '_blank');
                  }}
                  title={t('actions.print_label', 'Imprimir Etiqueta')}
                >
                  <Printer className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        );
      },
    });

    return baseColumns;
  }, [t]);

  // Filtrar columnas según selección (si se proporciona)
  const filteredColumnDefs = useMemo(() => {
    if (selectedColumns.length === 0) return allColumnDefs;
    return allColumnDefs.filter((col) => selectedColumns.includes(col.field || col.headerName || ''));
  }, [allColumnDefs, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs,
  };
};
